import { useEffect, useState } from 'react';

import { OverlayModal } from '~src/components/Modal/OverlayModal';
import { SendOfferModalNorlys } from '~src/components/Modal/sendOffer';

import { useDisplayContext } from '~src/context/DisplayContext';
import { PublicCalculatorContext, PublicCalculatorContextProvider } from '~src/context/PublicCalculatorContext';
import { useRegisterLead } from '~src/hooks/graphql/useLead';
import { formatSendOffersParameters, useInsertAndSendOffers } from '~src/hooks/graphql/useOffer';
import { useFinancingProvider } from '~src/hooks/useFinancingProvider';
import { LEAD_ORIGIN_NORLYS } from '~src/hooks/useLeadOrigin';

import { captureException } from '~src/sentry';
import { useAnalytics } from '~src/tracking/ga';

import { useCustomerContext } from '~src/context/CustomerContext';

import { CalculatorPageProps } from '~src/model';
import { InformationPageMobileNorlys } from './mobile';
import { InformationPageDesktopNorlys } from './desktop';
import { usePublicCalculator } from '~src/hooks/NorlysInformationPage';

export const InformationPage = ({ standalone }: CalculatorPageProps) => {
  const { leadOrigin } = useDisplayContext();

  const isNorlys = leadOrigin === LEAD_ORIGIN_NORLYS;

  return isNorlys ? <InformationPageNorlys standalone={standalone} /> : null;
};

const InformationPageNorlys = ({ standalone }: CalculatorPageProps) => {
  const { isMobile, leadOrigin } = useDisplayContext();
  const { provider: financingProvider } = useFinancingProvider();
  const { automaticQuoteSent } = useAnalytics();

  const { fetching: isSendingOffer, insertOffers: sendOffers } = useInsertAndSendOffers();
  const { fetching: isRegisteringLead } = useRegisterLead();

  const [sendOfferModalVisible, setSendOfferModalVisible] = useState(false);
  const [modalShownOnce, setModalShownOnce] = useState(false);

  const publicCalculatorContextValues = usePublicCalculator(standalone);

  const { residence, address, offerProducts } = publicCalculatorContextValues;

  const { customer } = useCustomerContext();

  useEffect(
    function initializeModalVisibility() {
      if (modalShownOnce) {
        return;
      }

      setSendOfferModalVisible(true);
      setModalShownOnce(true);
    },
    [modalShownOnce, offerProducts?.heatPumpOutdoorUnit]
  );

  const handleModalConfirm = async () => {
    try {
      automaticQuoteSent(customer?.email ?? undefined, leadOrigin);
    } catch (error) {
      captureException(error);
    }

    await sendOffers(
      formatSendOffersParameters({
        residence,
        address,
        customer,
        leadOrigin,
        financingProvider,
        offerProducts,
      })
    );
  };

  const isLoading = isRegisteringLead || isSendingOffer;

  return (
    <PublicCalculatorContextProvider value={publicCalculatorContextValues as PublicCalculatorContext}>
      {isMobile ? <InformationPageMobileNorlys /> : <InformationPageDesktopNorlys />}

      <OverlayModal visible={sendOfferModalVisible} background='blurWhite'>
        <SendOfferModalNorlys
          isLoading={isLoading}
          visible={sendOfferModalVisible}
          onConfirm={async () => {
            await handleModalConfirm();
            setSendOfferModalVisible(false);
          }}
        />
      </OverlayModal>
    </PublicCalculatorContextProvider>
  );
};
